import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import {
  ArrowLeft, Search, Download, Loader2, Inbox, X, User, CheckCircle,
  Clock, Archive, MessageSquarePlus,
} from "lucide-react";

const SUB_STATUS = {
  new: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  resolved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  archived: "bg-slate-600/20 text-slate-400 border-slate-600/30",
};
const TABS = ["all", "new", "pending", "resolved", "archived"];

export default function FormSubmissions() {
  const { formId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState({ submissions: [], total: 0, counts: {}, fields: [], form_name: "" });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [toast, setToast] = useState("");

  const notify = (m) => { setToast(m); setTimeout(() => setToast(""), 2500); };

  const fetchList = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status !== "all") params.append("status", status);
    if (search) params.append("search", search);
    axios.get(`${API}/admin/forms/${formId}/submissions?${params}`, { withCredentials: true })
      .then((r) => {
        if (r.data.managed) { nav("/admin/channel-submissions"); return; }
        setData(r.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [formId, status, search, nav]);
  useEffect(() => { fetchList(); }, [fetchList]);

  const patch = async (sid, body) => {
    await axios.patch(`${API}/admin/forms/${formId}/submissions/${sid}`, body, { withCredentials: true });
    fetchList();
    if (detail?.id === sid) {
      const r = await axios.get(`${API}/admin/forms/${formId}/submissions/${sid}`, { withCredentials: true });
      setDetail(r.data);
    }
  };

  const addNote = async (sid) => { if (!note.trim()) return; await patch(sid, { note }); setNote(""); notify("Note added"); };
  const exportCsv = () => window.open(`${API}/admin/forms/${formId}/submissions/export/csv`, "_blank");

  return (
    <div data-testid="form-submissions-page">
      {toast && <div className="fixed top-4 right-4 z-[200] bg-slate-800 border border-slate-700 text-white text-sm px-4 py-2.5 rounded-xl shadow-xl">{toast}</div>}

      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin/forms" className="p-2 rounded-lg bg-slate-900 border border-slate-800 text-slate-400 hover:text-white"><ArrowLeft size={18} /></Link>
        <h2 className="text-xl font-bold text-white">{data.form_name || "Submissions"}</h2>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {["new", "pending", "resolved", "archived"].map((k) => (
          <div key={k} className="bg-slate-900 rounded-xl border border-slate-800 p-4">
            <div className="text-2xl font-bold text-white">{data.counts?.[k] || 0}</div>
            <div className="text-xs text-slate-500 capitalize">{k}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <input placeholder="Search submissions..." data-testid="sub-search" value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-800 bg-slate-900 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
        </div>
        <button onClick={exportCsv} data-testid="sub-export" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-500 whitespace-nowrap"><Download size={16} /> Export CSV</button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map((t) => (
          <button key={t} data-testid={`sub-tab-${t}`} onClick={() => setStatus(t)}
            className={cn("px-3 py-1.5 rounded-lg text-xs font-medium border capitalize", status === t ? "bg-blue-600 text-white border-blue-500" : "bg-slate-900 text-slate-400 border-slate-800 hover:text-white")}>
            {t}{t !== "all" && data.counts?.[t] ? ` (${data.counts[t]})` : ""}
          </button>
        ))}
      </div>

      <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="sub-table">
            <thead><tr className="bg-slate-950/50 border-b border-slate-800">
              <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Preview</th>
              <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Date</th>
              <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Status</th>
              <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase text-right"></th>
            </tr></thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan={4} className="px-4 py-12 text-center"><Loader2 className="animate-spin mx-auto text-slate-500" /></td></tr>
              ) : data.submissions.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-16 text-center"><Inbox className="mx-auto text-slate-700 mb-3" size={40} /><p className="text-slate-500">No submissions</p></td></tr>
              ) : data.submissions.map((s) => (
                <tr key={s.id} className="hover:bg-slate-800/40 cursor-pointer" data-testid={`sub-row-${s.id}`} onClick={() => setDetail(s)}>
                  <td className="px-4 py-3 text-sm text-slate-300 max-w-md truncate">{Object.entries(s.values || {}).slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(" • ")}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{(s.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3"><span className={cn("px-2 py-0.5 rounded border text-[11px] font-bold capitalize", SUB_STATUS[s.status])}>{s.status}</span></td>
                  <td className="px-4 py-3 text-right"><button className="text-blue-400 text-xs">Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <div className="fixed inset-0 z-[100] flex justify-end bg-slate-950/70 backdrop-blur-sm" onClick={() => setDetail(null)}>
          <div className="w-full max-w-lg bg-slate-900 h-full overflow-y-auto border-l border-slate-800 animate-slideLeft" onClick={(e) => e.stopPropagation()} data-testid="sub-detail">
            <div className="sticky top-0 bg-slate-950/95 border-b border-slate-800 p-5 flex items-center justify-between">
              <div className="flex items-center gap-2"><h3 className="font-bold text-white">Submission</h3><span className={cn("px-2 py-0.5 rounded border text-[11px] font-bold capitalize", SUB_STATUS[detail.status])}>{detail.status}</span></div>
              <button onClick={() => setDetail(null)} className="text-slate-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex flex-wrap gap-2">
                <button onClick={() => patch(detail.id, { assign_self: true })} data-testid="sub-assign" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 text-slate-200 text-xs hover:bg-slate-700"><User size={14} /> Assign to me</button>
                <button onClick={() => patch(detail.id, { status: "pending" })} data-testid="sub-pending" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500/10 text-amber-400 text-xs hover:bg-amber-500/20"><Clock size={14} /> Pending</button>
                <button onClick={() => patch(detail.id, { status: "resolved" })} data-testid="sub-resolve" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs hover:bg-emerald-500/20"><CheckCircle size={14} /> Resolve</button>
                <button onClick={() => patch(detail.id, { status: "archived" })} data-testid="sub-archive" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 text-slate-400 text-xs hover:bg-slate-700"><Archive size={14} /> Archive</button>
              </div>
              {detail.assigned_to_name && <p className="text-xs text-slate-500">Assigned to {detail.assigned_to_name}</p>}
              <div className="space-y-2">
                {Object.entries(detail.values || {}).map(([k, v]) => (
                  <div key={k} className="rounded-lg bg-slate-950 px-3 py-2">
                    <div className="text-xs text-slate-500">{k}</div>
                    <div className="text-sm text-slate-200 break-words">{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-800 pt-4">
                <h4 className="text-sm font-semibold text-white mb-2">Internal Notes</h4>
                <div className="space-y-2 mb-3">
                  {(detail.notes || []).length === 0 && <p className="text-xs text-slate-500">No notes.</p>}
                  {(detail.notes || []).map((n) => (
                    <div key={n.id} className="rounded-lg bg-slate-950 px-3 py-2"><div className="text-sm text-slate-200">{n.text}</div><div className="text-[11px] text-slate-500 mt-1">{n.author_name} • {(n.created_at || "").slice(0, 16).replace("T", " ")}</div></div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note..." data-testid="sub-note-input" className="flex-grow px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                  <button onClick={() => addNote(detail.id)} data-testid="sub-note-add" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-500"><MessageSquarePlus size={14} /> Add</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
